import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useSession } from "@/stores/session";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/password-input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/signup")({
  beforeLoad: async () => {
    const s = useSession.getState();
    if (!s.hydrated) { try { await s.hydrate(); } catch {} }
    if (useSession.getState().user) throw redirect({ to: "/" });
  },
  component: SignupPage,
});

export function SignupPage() {
  const nav = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [account, setAccount] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const me = await authApi.signup(email, password, account);
      setSession(me);
      void nav({ to: "/" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErr("Email already registered");
      else if (e instanceof ApiError && e.status === 400) setErr("Please check your inputs");
      else setErr("Sign up failed. Please try again.");
    } finally { setLoading(false); }
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Spin up a deleggate environment in seconds."
      footer={<>Have an account? <a href="/login" className="font-medium text-brand hover:underline">Sign in</a></>}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="account">Account name</Label>
          <Input id="account" required value={account} onChange={(e) => setAccount(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <PasswordInput id="password" required autoComplete="new-password" minLength={8}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {err && <p role="alert" className="text-sm text-fail">{err}</p>}
        <Button type="submit" className="w-full bg-brand text-primary-foreground hover:bg-brand/90" disabled={loading}>
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
